/**
 * Small dialogs shared by the Triage Call Queue (bulk review) and Call Detail
 * (assign / escalate / approve): a note box, and a coach picker.
 */
import { useState } from 'react';
import { coachOptions, ISSUE_TYPES, defaultCallbackAt } from '../../lib/triage';

function Dialog({ title, onClose, children, footer }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 'min(480px, 95vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex-between" style={{ padding: '14px 20px' }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <button type="button" className="btn small" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={{ padding: '0 20px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {children}
        </div>
        <div className="modal-footer flex-between" style={{ padding: '14px 20px' }}>{footer}</div>
      </div>
    </div>
  );
}

/** Tag a call (or one transcript line) with an issue type and a note. */
export function AnnotateDialog({ lineText, busy, onConfirm, onClose }) {
  const [tag, setTag] = useState('HALLUCINATION');
  const [note, setNote] = useState('');
  return (
    <Dialog
      title="Annotate"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="button" className="btn small primary" disabled={busy} onClick={() => onConfirm({ tag, note })}>
            {busy ? 'Saving…' : 'Save annotation'}
          </button>
        </>
      )}
    >
      {lineText && (
        <div className="text-xs text-muted">On the line: “{lineText.length > 140 ? `${lineText.slice(0, 140)}…` : lineText}”</div>
      )}
      <div className="form-field">
        <label>Issue type</label>
        <select value={tag} onChange={(e) => setTag(e.target.value)}>
          {ISSUE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label>Note</label>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

/** Add a "AI said X — should have said Y" pattern to the coaching library. */
export function PatternDialog({ initial, busy, onConfirm, onClose }) {
  const [tag, setTag] = useState(initial?.tag || 'HALLUCINATION');
  const [title, setTitle] = useState(initial?.title || '');
  const [badText, setBadText] = useState(initial?.badText || '');
  const [goodText, setGoodText] = useState(initial?.goodText || '');
  const ready = title.trim() && (badText.trim() || goodText.trim());
  return (
    <Dialog
      title="Add to coaching library"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn small primary"
            disabled={busy || !ready}
            onClick={() => onConfirm({ tag, title, badText, goodText })}
          >
            {busy ? 'Saving…' : 'Add pattern'}
          </button>
        </>
      )}
    >
      <div className="form-field">
        <label>Issue type</label>
        <select value={tag} onChange={(e) => setTag(e.target.value)}>
          {ISSUE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label>Short title</label>
        <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}
               placeholder="e.g. Offered a slot without checking the diary" />
      </div>
      <div className="form-field">
        <label>What the AI said</label>
        <textarea rows={3} value={badText} onChange={(e) => setBadText(e.target.value)} />
      </div>
      <div className="form-field">
        <label>What it should have said</label>
        <textarea rows={3} value={goodText} onChange={(e) => setGoodText(e.target.value)} />
      </div>
    </Dialog>
  );
}

/** Schedule a human callback as a task for a team member. */
export function CallbackDialog({ customerLabel, options, loading, busy, onConfirm, onClose }) {
  const [assigneeId, setAssigneeId] = useState('');
  const [when, setWhen] = useState(() => defaultCallbackAt());
  const [note, setNote] = useState('');
  return (
    <Dialog
      title={`Schedule a callback${customerLabel ? ` to ${customerLabel}` : ''}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn small primary"
            disabled={busy || !assigneeId || !when}
            onClick={() => onConfirm({ assigneeId, when, note })}
          >
            {busy ? 'Saving…' : 'Create task'}
          </button>
        </>
      )}
    >
      <div className="text-xs text-muted">Creates a call task for the person you choose.</div>
      <div className="form-field">
        <label>Who should call back?</label>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} disabled={loading}>
          <option value="">{loading ? 'Loading…' : 'Choose a team member…'}</option>
          {(options || []).map((o) => (
            <option key={o.id} value={o.id}>{o.primary}{o.meta ? ` · ${o.meta}` : ''}</option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label>When</label>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
      </div>
      <div className="form-field">
        <label>Notes (optional)</label>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

/** Ask for an optional note, then confirm. */
export function NoteDialog({ title, intro, confirmLabel, busy, onConfirm, onClose, noteLabel = 'Note (optional)' }) {
  const [note, setNote] = useState('');
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="button" className="btn small primary" disabled={busy} onClick={() => onConfirm(note)}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </>
      )}
    >
      {intro && <div className="text-xs text-muted">{intro}</div>}
      <div className="form-field">
        <label>{noteLabel}</label>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

/** Pick a coach, an optional due date and note. */
export function CoachDialog({ title, intro, options, loading, busy, onConfirm, onClose }) {
  const coaches = coachOptions(options);
  const [coachId, setCoachId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const picked = coaches.find((c) => String(c.id) === String(coachId));

  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn small primary"
            disabled={busy || !picked}
            onClick={() => onConfirm({
              coachUserId: picked.id, coachName: picked.primary, dueDate: dueDate || null, note,
            })}
          >
            {busy ? 'Saving…' : 'Assign'}
          </button>
        </>
      )}
    >
      {intro && <div className="text-xs text-muted">{intro}</div>}
      <div className="form-field">
        <label>Coach</label>
        <select value={coachId} onChange={(e) => setCoachId(e.target.value)} disabled={loading}>
          <option value="">{loading ? 'Loading…' : 'Choose a coach…'}</option>
          {coaches.map((c) => (
            <option key={c.id} value={c.id}>{c.primary}{c.meta ? ` · ${c.meta}` : ''}</option>
          ))}
        </select>
        {!loading && coaches.length === 0 && (
          <div className="text-xs text-muted" style={{ marginTop: 4 }}>No team members found to assign to.</div>
        )}
      </div>
      <div className="form-field">
        <label>Due date (optional)</label>
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </div>
      <div className="form-field">
        <label>What should they focus on? (optional)</label>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}
